import React from 'react';
import { Line, Circle, Group as KonvaGroup, Group } from 'react-konva';
import DrawingText from './DrawingText';
import VertexHandle from './VertexHandle';
import SegmentMeasurementOverlay from './SegmentMeasurementOverlay';
import { resolveAllParams } from '../../ShapeList/paramUtils';
import {
  getDisplayMeasurement,
  enhanceShapeWithMeasurements,
} from '../utils/drawingUtils';
import {
  DISPLAY_KEY_SEGMENT_LENGTH,
  DISPLAY_KEY_MEASUREMENT,
  DISPLAY_KEY_AREA,
} from './ShapeRenderer';

const SELECTED_GLOW = {
  shadowColor: '#3B82F6',
  shadowBlur: 18,
  shadowOpacity: 0.7,
  shadowOffsetX: 0,
  shadowOffsetY: 0,
};

const PolylineShape = ({
  shape,
  commonProps,
  isSelected,
  stageScale,
  activeStrokeColor,
  vertexMarkerRadius,
  selectedStrokeHighlightColor,
  hoverColor,
  onVertexClick,
  shouldShowText,
  displayKeys,
  textFontSize,
  shapeScaleProps,
  dpi,
  imageSize,
  displayUnitSystem,
  isDrawingPreview,
  vertexEditInfo,
  showOverlay = true,
  hoveredSegmentInfo,
}) => {
  const scaledShape = shape;

  const isTextTooSmall = (text, metricLength, factor = 1.1) => {
    if (isSelected) return false;
    const estimatedTextWidth = String(text).length * textFontSize * 0.6;
    return metricLength < estimatedTextWidth * factor;
  };

  const enhancedForDisplay = enhanceShapeWithMeasurements(
    scaledShape,
    shapeScaleProps,
    dpi,
    imageSize,
    displayUnitSystem,
  );
  const polylinePoints = enhancedForDisplay.points || [];
  const sumX = polylinePoints.reduce(
    (sum, val, i) => (i % 2 === 0 ? sum + val : sum),
    0,
  );
  const sumY = polylinePoints.reduce(
    (sum, val, i) => (i % 2 !== 0 ? sum + val : sum),
    0,
  );
  const numPoints = polylinePoints.length / 2;
  const polylineCentroid =
    numPoints > 0
      ? { x: sumX / numPoints, y: sumY / numPoints }
      : { x: 0, y: 0 };

  const totalDisplay = enhancedForDisplay.displayTotalMeasurement
    ? `${enhancedForDisplay.displayTotalMeasurement.value} ${enhancedForDisplay.displayTotalMeasurement.unit}`
    : '';

  const calculatedParamsForPolyline = {
    TotalLength: totalDisplay,
    Length: totalDisplay,
    'Total Length': totalDisplay,
    [DISPLAY_KEY_MEASUREMENT]: totalDisplay,
  };
  const allResolvedParamsForPolyline = resolveAllParams(
    shape.params,
    calculatedParamsForPolyline,
  );

  return (
    <KonvaGroup {...commonProps}>
      <Line
        name="visual_shape"
        points={scaledShape.points}
        stroke={commonProps.stroke}
        strokeWidth={commonProps.strokeWidth}
        hitStrokeWidth={Math.max(15, commonProps.strokeWidth)}
        lineCap="round"
        lineJoin="round"
        listening={true}
        perfectDrawEnabled={false}
        {...(isSelected ? SELECTED_GLOW : {})}
      />
      {polylinePoints &&
        polylinePoints.length >= 4 &&
        enhancedForDisplay.segmentDistances &&
        enhancedForDisplay.segmentDistances.map((dist, index) => {
          const p1x = scaledShape.points[index * 2];
          const p1y = scaledShape.points[index * 2 + 1];
          const p2x = scaledShape.points[(index + 1) * 2];
          const p2y = scaledShape.points[(index + 1) * 2 + 1];

          if (typeof p2x === 'undefined' || typeof p2y === 'undefined')
            return null;

          const midX = (p1x + p2x) / 2;
          const midY = (p1y + p2y) / 2;
          const angleRad = Math.atan2(p2y - p1y, p2x - p1x);
          let angleDeg = angleRad * (180 / Math.PI);

          if (angleDeg > 90) angleDeg -= 180;
          else if (angleDeg < -90) angleDeg += 180;

          const measurement = getDisplayMeasurement(
            dist,
            shapeScaleProps,
            dpi,
            imageSize,
            displayUnitSystem,
          );
          const segmentDisplay = `${measurement.value} ${measurement.unit}`;
          const perSegmentResolved = resolveAllParams(
            shape.params,
            { TotalLength: segmentDisplay, SegmentLength: segmentDisplay, Length: segmentDisplay },
          );

          const isSegmentBeingDrawn = isDrawingPreview && (index === numPoints - 2);
          const isSegmentBeingEdited = isSelected && vertexEditInfo && vertexEditInfo.shapeId === shape.id && !vertexEditInfo.isHole && (index === vertexEditInfo.vertexIndex - 1 || index === vertexEditInfo.vertexIndex);
          const showSegmentOverlay = isSegmentBeingDrawn || isSegmentBeingEdited;

          const showSegmentText = !showSegmentOverlay && shouldShowText && displayKeys.includes(DISPLAY_KEY_SEGMENT_LENGTH) && !isTextTooSmall(segmentDisplay, dist, 0.8);

          if (!showSegmentOverlay && !showSegmentText) return null;

          return (
            <Group key={`text-group-${scaledShape.id}-${index}`}>
              {showSegmentOverlay && (
                <SegmentMeasurementOverlay
                  x1={p1x}
                  y1={p1y}
                  x2={p2x}
                  y2={p2y}
                  activeStrokeColor={activeStrokeColor}
                  stageScale={stageScale}
                  textFontSize={textFontSize}
                  shapeScaleProps={shapeScaleProps}
                  dpi={dpi}
                  imageSize={imageSize}
                  displayUnitSystem={displayUnitSystem}
                  showOverlay={showOverlay}
                />
              )}
              {showSegmentText && (
                <DrawingText
                  key={`polyline-dist-${scaledShape.id}-${index}`}
                  x={midX}
                  y={midY}
                  text={segmentDisplay}
                  fontSize={textFontSize}
                  fill={activeStrokeColor}
                  rotation={angleDeg}
                  offsetY={-textFontSize * 0.8}
                  offsetX={0}
                  stageScale={stageScale}
                  useBadge={true}
                  badgeStroke={activeStrokeColor}
                />
              )}
              {shouldShowText && displayKeys.length > 0 && perSegmentResolved && displayKeys
                .filter((dk) => dk !== DISPLAY_KEY_SEGMENT_LENGTH && dk !== DISPLAY_KEY_MEASUREMENT && dk !== DISPLAY_KEY_AREA)
                .map((dk, dkIdx) => {
                  const val = perSegmentResolved[dk];
                  if (!val) return null;

                  const offsetMultiplier = 0.8 + 1.45 * dkIdx;

                  return (
                    <DrawingText
                      key={`polyline-dk-${scaledShape.id}-${index}-${dkIdx}`}
                      x={midX}
                      y={midY}
                      text={`${String(val)}`}
                      fontSize={textFontSize}
                      fill={activeStrokeColor}
                      rotation={angleDeg}
                      offsetY={textFontSize * offsetMultiplier}
                      offsetX={0}
                      stageScale={stageScale}
                      useBadge={true}
                      badgeStroke={activeStrokeColor}
                    />
                  );
                })}
            </Group>
          );
        })}

      {shouldShowText && !isDrawingPreview && displayKeys.includes(DISPLAY_KEY_MEASUREMENT) && !isTextTooSmall(totalDisplay, enhancedForDisplay.totalDistance) && (
        <Group
          x={polylineCentroid.x}
          y={polylineCentroid.y}
          listening={false}
        >
          {displayKeys
            .filter((dk) => dk === DISPLAY_KEY_MEASUREMENT)
            .map((dk, dkIdx) => {
              const val = allResolvedParamsForPolyline[dk];
              if (!val) return null;
              return (
                <DrawingText
                  key={`polyline-summary-${scaledShape.id}-${dkIdx}`}
                  x={0}
                  y={0}
                  text={String(val)}
                  fontSize={textFontSize}
                  fill={activeStrokeColor}
                  align="center"
                  verticalAlign="middle"
                  offsetX={0}
                  offsetY={0}
                  stageScale={stageScale}
                  useBadge={true}
                  badgeStroke={activeStrokeColor}
                />
              );
            })}
        </Group>
      )}

      {hoveredSegmentInfo &&
        hoveredSegmentInfo.shapeId === shape.id &&
        !vertexEditInfo && (
          <Group listening={false}>
            <Circle
              x={hoveredSegmentInfo.x - shape.x}
              y={hoveredSegmentInfo.y - shape.y}
              radius={vertexMarkerRadius * 1.4}
              fill="#3B82F6"
              stroke="#FFFFFF"
              strokeWidth={Math.max(0.5, 1 / stageScale)}
              perfectDrawEnabled={false}
            />
            <Circle
              x={hoveredSegmentInfo.x - shape.x}
              y={hoveredSegmentInfo.y - shape.y}
              radius={vertexMarkerRadius * 0.5}
              fill="#FFFFFF"
              perfectDrawEnabled={false}
            />
          </Group>
        )}

      {isSelected &&
        scaledShape.points &&
        scaledShape.points.map((_, index) => {
          if (index % 2 === 0) {
            const vertexX = scaledShape.points[index];
            const vertexY = scaledShape.points[index + 1];
            return (
              <VertexHandle
                key={`vtx-${scaledShape.id}-${index / 2}`}
                x={vertexX}
                y={vertexY}
                radius={vertexMarkerRadius}
                color={selectedStrokeHighlightColor}
                hoverColor={hoverColor}
                onMouseDown={(e) =>
                  onVertexClick(e, scaledShape.id, index / 2)
                }
                onTouchStart={(e) =>
                  onVertexClick(e, scaledShape.id, index / 2)
                }
                stageScale={stageScale}
              />
            );
          }
          return null;
        })}
    </KonvaGroup>
  );
};

export default PolylineShape;
